import { useMutation } from '@tanstack/react-query';
import React, { FC, useEffect, useState } from 'react';

import { Icon } from '@column/column-ui-kit';
import { Button, InputGroup, InputGroupAddon, InputGroupInput, Spinner } from '@column/ui';

import { UserModel } from '~/models';
import { UserRepository } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { log } from '~/util';

import { FieldStatus, TRAILING_STATUS_ADDON_CLASS_NAME } from './FieldStatus';

const SENT_CONFIRMATION_MS = 2000;

interface EmailFieldProps {
  user: UserModel;
  value: string;
}

export const EmailField: FC<EmailFieldProps> = ({ user, value }) => {
  const refetchCurrentUser = useSessionStore((state) => state.refetchCurrentUser);
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const [justSent, setJustSent] = useState(false);

  const isVerified = user.emailState === 'VERIFIED';
  const isPending = user.emailState === 'VERIFY_IN_PROGRESS';

  useEffect(() => {
    if (!justSent) {
      return undefined;
    }
    const timer = setTimeout(() => setJustSent(false), SENT_CONFIRMATION_MS);
    return () => clearTimeout(timer);
  }, [justSent]);

  const sendVerificationEmail = useMutation<unknown, unknown, { resend: boolean }>({
    mutationFn: () => UserRepository.createVerifyEmail(),
    onSuccess: (_data, { resend }) => {
      log({
        name: `User email verify${resend ? ' resent' : ''}`,
        context: user,
      });
      addSuccessNotification({
        content: resend ? 'Verification email resent! Check your inbox.' : 'Verification email sent! Check your inbox.',
      });
      setJustSent(true);
      void refetchCurrentUser();
    },
    onError: (error: unknown) => {
      addDangerNotification({
        content: error instanceof Error ? error.message : 'Failed to send the verification email',
      });
    },
  });

  return (
    <InputGroup className="h-10">
      <InputGroupInput
        value={value}
        placeholder="Email Address"
        aria-label="Email address"
        autoComplete="off"
        readOnly
        className="px-4"
      />
      <InputGroupAddon align="inline-end" className={TRAILING_STATUS_ADDON_CLASS_NAME}>
        {isVerified ? (
          <FieldStatus size="xs" tone="success" label="Verified" />
        ) : isPending ? (
          <FieldStatus
            size="xs"
            tone="pending"
            label="Pending"
            tooltip="A verification email has been sent. Please check your inbox to complete verification."
          />
        ) : (
          <FieldStatus
            size="xs"
            tone="warning"
            label="Not verified"
            tooltip="Please verify your email address. Verification is required to set up two-factor authentication."
          />
        )}

        {!isVerified && (
          <Button
            type="button"
            variant="muted"
            size="xs"
            data-cy="email-verify-button"
            disabled={sendVerificationEmail.isPending || justSent}
            onClick={() => sendVerificationEmail.mutate({ resend: isPending })}
            className="min-w-[72px] disabled:opacity-100"
          >
            {sendVerificationEmail.isPending ? (
              <Spinner className="size-3" />
            ) : justSent ? (
              <Icon.Checkmark className="text-seafoam-600 [--icon-size-x:14px] [--icon-size-y:14px] dark:text-seafoam-400" />
            ) : (
              <Icon.Mail className="[--icon-size-x:14px] [--icon-size-y:14px]" />
            )}
            {sendVerificationEmail.isPending ? 'Sending…' : justSent ? 'Sent' : isPending ? 'Resend' : 'Verify'}
          </Button>
        )}
      </InputGroupAddon>
    </InputGroup>
  );
};
